import React, {useCallback, useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Button} from '@/components/ui/button'
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
} from '@/components/ui/accordion'
import {useCioFacetSelector, SET_CIO_SELECTED_FACETS} from '@/context/cio-facet'
import {filterAndRenameCioFacets} from '@/utils/facetHelpers'
import {FilterPriceRangeInput} from './FilterPriceRangeInputSection'
import {getNewSelectedFilters, handlePriceInputChangeHelper} from './helpers.cio.drawerContent'
import {FacetOptionsSection} from './FilterOptionsSection'
interface CioFiltersDrawerContentProps {
    attributes?: React.HTMLAttributes<HTMLDivElement>
    onOpenChange: () => void
    'data-testid'?: string
}

export const CioFiltersDrawerContent: React.FC<CioFiltersDrawerContentProps> = React.memo(
    ({attributes, onOpenChange, 'data-testid': dataTestId}) => {
        const params = new URLSearchParams(window.location.search)
        const [priceRange, setPriceRange] = useState<{min?: string; max?: string}>({
            min: params.get('pmin') || undefined,
            max: params.get('pmax') || undefined
        })
        const [showMore, setShowMore] = useState<{[key: string]: boolean}>({})
        const {cioFacets, totalResults, customSitePreferences, selectedCioFacets} =
            useCioFacetSelector((context) => context?.state)
        const dispatchSelectedFacets = useCioFacetSelector((context) => context?.dispatch)

        const filteredAndRenamedCioFacets = useMemo(() => {
            const Constructor_FacetMapping = customSitePreferences.data?.find(
                (item: any) => item.id === 'Constructor_FacetMapping'
            )
            const facetMapping = Constructor_FacetMapping?.value
                ? JSON.parse(Constructor_FacetMapping?.value)
                : {}
            return filterAndRenameCioFacets(cioFacets, facetMapping)
        }, [cioFacets, customSitePreferences.data])

        interface CheckboxChangeOption {
            value: string
            display_name: string
            count: number
        }

        const handleCheckboxChange = useCallback(
            (category: string, option: CheckboxChangeOption) => {
                // Use a functional update to ensure we always have the latest state
                const newSelectedFilters = getNewSelectedFilters(
                    selectedCioFacets,
                    category,
                    option
                )

                const wouldChange =
                    JSON.stringify(selectedCioFacets) !== JSON.stringify(newSelectedFilters)

                if (wouldChange) {
                    dispatchSelectedFacets({
                        type: SET_CIO_SELECTED_FACETS,
                        payload: newSelectedFilters
                    })
                }
            },
            [selectedCioFacets, dispatchSelectedFacets]
        )

        const handleShowMoreToggle = useCallback((category: string) => {
            setShowMore((prev) => ({
                ...prev,
                [category]: !prev[category]
            }))
        }, [])

        const handlePriceInputChange = useCallback(
            (type: 'min' | 'max', value: string) => {
                handlePriceInputChangeHelper(
                    type,
                    value,
                    priceRange,
                    selectedCioFacets,
                    setPriceRange,
                    dispatchSelectedFacets
                )
            },
            [priceRange, selectedCioFacets, setPriceRange, dispatchSelectedFacets]
        )

        return (
            <div className="relative h-full" {...attributes}>
                <h2 className="text-lg font-bold mb-4 p-4">Filters</h2>
                {filteredAndRenamedCioFacets?.map((facet: any) => (
                    <Accordion
                        type="multiple"
                        key={facet.name}
                        data-testid={dataTestId || 'CioFiltersDrawerContent-Accordion'}
                    >
                        <AccordionItem
                            value={facet.name}
                            data-testid={dataTestId || 'CioFiltersDrawerContent-AccordionItem'}
                        >
                            <AccordionTrigger
                                className="pl-4 pr-4 font-semibold"
                                data-testid={
                                    dataTestId || 'CioFiltersDrawerContent-AccordionTrigger'
                                }
                            >
                                {facet.display_name}
                            </AccordionTrigger>
                            <AccordionContent
                                className="pl-4 pr-4"
                                data-testid={
                                    dataTestId || 'CioFiltersDrawerContent-AccordionContent'
                                }
                            >
                                {facet.name === 'price' ? (
                                    <FilterPriceRangeInput
                                        data-testid={
                                            dataTestId || 'CioFiltersDrawerContent-PriceRange'
                                        }
                                        priceRange={priceRange}
                                        minOnChange={(e: any) =>
                                            handlePriceInputChange('min', e.target.value)
                                        }
                                        maxOnChange={(e: any) =>
                                            handlePriceInputChange('max', e.target.value)
                                        }
                                    />
                                ) : (
                                    <FacetOptionsSection
                                        facet={facet}
                                        showMore={showMore}
                                        onShowMoreToggle={handleShowMoreToggle}
                                        selectedCioFacets={selectedCioFacets}
                                        onCheckboxChange={handleCheckboxChange}
                                        dataTestId={dataTestId}
                                        data-testid={
                                            dataTestId ||
                                            'CioFiltersDrawerContent-FacetOptionsSection'
                                        }
                                    />
                                )}
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                ))}
                <div className="bottom-0 left-0 w-full flex justify-center bg-white border-t">
                    <Button
                        ctaType="button"
                        variant="primary"
                        className="m-4 p-4 w-full"
                        onClick={onOpenChange}
                        data-testid={dataTestId || 'CioFiltersDrawerContent-Button'}
                    >
                        Show Results {totalResults.responseTotal}
                    </Button>
                </div>
            </div>
        )
    }
)

CioFiltersDrawerContent.displayName = 'CioFiltersDrawerContent'

CioFiltersDrawerContent.propTypes = {
    attributes: PropTypes.object,
    onOpenChange: PropTypes.func.isRequired,
    'data-testid': PropTypes.string
}
